import React, { useState } from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { Employee, ProjectFormData } from '../../types';
import { formatDate } from '../../lib/formatDate';
import { POSITIONS, CURRENCIES, UNITS } from '../../constants';

interface ProjectEmployeeManagerProps {
  employees: Employee[];
  projectEmployees: ProjectFormData['employees'];
  onEmployeesChange: (employees: ProjectFormData['employees']) => void;
  disabled?: boolean;
  projectCurrency?: string;
}

const ProjectEmployeeManager: React.FC<ProjectEmployeeManagerProps> = ({
  employees,
  projectEmployees,
  onEmployeesChange,
  disabled = false,
  projectCurrency = CURRENCIES.EUR
}) => {
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [role, setRole] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [price, setPrice] = useState<number | ''>('');
  const [unit, setUnit] = useState('hour');
  const [shadow, setShadow] = useState(false);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editRole, setEditRole] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editPrice, setEditPrice] = useState<number | ''>('');
  const [editUnit, setEditUnit] = useState('hour');
  const [editShadow, setEditShadow] = useState(false);

  // Get available roles from predefined positions
  const getAvailableRoles = () => {
    return POSITIONS;
  };

  // Get currency symbol
  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case CURRENCIES.EUR: return '€';
      case CURRENCIES.USD: return '$';
      case CURRENCIES.GBP: return '£';
      case CURRENCIES.RSD: return '';
      case CURRENCIES.CHF: return '';
      default: return currency;
    }
  };

  // Handle employee selection and prepopulate role
  const handleEmployeeSelection = (employee: Employee | null) => {
    setSelectedEmployee(employee);
    if (employee && employee.position) {
      setRole(employee.position);
    } else {
      setRole('');
    }
  };

  const handleAddEmployee = () => {
    if (!selectedEmployee) return;

    const newEmployee = {
      employeeId: selectedEmployee.id,
      role: role || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      price: price !== '' && price !== null ? Number(price) : undefined,
      unit: unit || undefined,
      shadow
    };

    const updatedEmployees = [...(projectEmployees || []), newEmployee];
    onEmployeesChange(updatedEmployees);

    // Reset form
    setSelectedEmployee(null);
    setRole('');
    setStartDate('');
    setEndDate('');
    setPrice('');
    setUnit('hour');
    setShadow(false);
  };

  const handleRemoveEmployee = (index: number) => {
    const updatedEmployees = (projectEmployees || []).filter((_, i) => i !== index);
    onEmployeesChange(updatedEmployees);
  };

  const handleEditEmployee = (index: number) => {
    const employee = projectEmployees?.[index];
    if (!employee) return;

    setEditingIndex(index);
    setEditRole(employee.role || '');
    setEditStartDate(employee.startDate || '');
    setEditEndDate(employee.endDate || '');
    setEditPrice(employee.price || '');
    setEditUnit(employee.unit || 'hour');
    setEditShadow(employee.shadow || false);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (editingIndex === null || !projectEmployees) return;

    const updatedEmployees = [...projectEmployees];
    updatedEmployees[editingIndex] = {
      ...updatedEmployees[editingIndex],
      role: editRole || undefined,
      startDate: editStartDate || undefined,
      endDate: editEndDate || undefined,
      price: editPrice !== '' && editPrice !== null ? Number(editPrice) : undefined,
      unit: editUnit || undefined,
      shadow: editShadow
    };

    onEmployeesChange(updatedEmployees);
    setEditDialogOpen(false);
    setEditingIndex(null);
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingIndex(null);
    setEditRole('');
    setEditStartDate('');
    setEditEndDate('');
    setEditPrice('');
    setEditUnit('hour');
    setEditShadow(false);
  };

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown Employee';
  };

  const isEmployeeAlreadyAdded = (employeeId: string) => {
    return (projectEmployees || []).some(emp => emp.employeeId === employeeId);
  };

  const availableEmployees = employees.filter(emp => 
    emp.isActive && !isEmployeeAlreadyAdded(emp.id)
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" mb={2}>Project Team</Typography>

      {/* Employee List */}
      {(projectEmployees || []).length > 0 && (
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(projectEmployees || []).map((employee, index) => (
                <TableRow key={`${employee.employeeId}-${index}-${employee.shadow}`}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {getEmployeeName(employee.employeeId)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {employee.role || '-'}
                  </TableCell>
                  <TableCell>
                    {employee.startDate ? formatDate(employee.startDate) : '-'}
                  </TableCell>
                  <TableCell>
                    {employee.endDate ? formatDate(employee.endDate) : '-'}
                  </TableCell>
                  <TableCell>
                    {employee.price ? `${getCurrencySymbol(projectCurrency)}${employee.price}` : '-'}
                  </TableCell>
                  <TableCell>
                    {employee.unit || '-'}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const currentDate = new Date();
                      const endDate = employee.endDate ? new Date(employee.endDate) : null;
                      const isAssignmentEnded = endDate && endDate < currentDate;
                      
                      if (employee.shadow) {
                        return (
                          <Chip
                            label="Shadow"
                            color="default"
                            size="small"
                          />
                        );
                      } else if (isAssignmentEnded) {
                        return (
                          <Chip
                            label="Inactive"
                            color="error"
                            size="small"
                          />
                        );
                      } else {
                        return (
                          <Chip
                            label="Active"
                            color="primary"
                            size="small"
                          />
                        );
                      }
                    })()}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <IconButton
                        onClick={() => handleEditEmployee(index)}
                        disabled={disabled}
                        color="primary"
                        size="small"
                        title="Edit employee details"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleRemoveEmployee(index)}
                        disabled={disabled}
                        color="error"
                        size="small"
                        title="Remove employee"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Employee Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCancelEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Employee Details</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Edit the project details for {editingIndex !== null && projectEmployees?.[editingIndex] ? 
                getEmployeeName(projectEmployees[editingIndex].employeeId) : 'this employee'}
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
                disabled={disabled}
                label="Role"
              >
                {getAvailableRoles().map((roleOption) => (
                  <MenuItem key={roleOption} value={roleOption}>
                    {roleOption}
                  </MenuItem>
                ))}
                {!getAvailableRoles().includes(editRole as any) && editRole && (
                  <MenuItem value={editRole}>
                    {editRole}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <TextField
                label="Start Date"
                type="date"
                value={editStartDate}
                onChange={(e) => setEditStartDate(e.target.value)}
                disabled={disabled}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="End Date"
                type="date"
                value={editEndDate}
                onChange={(e) => setEditEndDate(e.target.value)}
                disabled={disabled}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <TextField
                label="Price"
                type="number"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value === '' ? '' : Number(e.target.value))}
                disabled={disabled}
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
              />
              <FormControl fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={editUnit}
                  onChange={(e) => setEditUnit(e.target.value)}
                  disabled={disabled}
                  label="Unit"
                >
                  <MenuItem value="hour">{UNITS.HOUR}</MenuItem>
                  <MenuItem value="day">{UNITS.DAY}</MenuItem>
                  <MenuItem value="month">{UNITS.MONTH}</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={editShadow}
                  onChange={(e) => setEditShadow(e.target.checked)}
                  disabled={disabled}
                />
              }
              label="Shadow Role"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} disabled={disabled}>
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={disabled}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Employee Form */}
      <Box sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Autocomplete
              options={availableEmployees}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
              value={selectedEmployee}
              onChange={(_, newValue) => handleEmployeeSelection(newValue)}
              disabled={disabled}
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Employee"
                  placeholder="Select an employee"
                />
              )}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                disabled={disabled}
                label="Role"
              >
                {getAvailableRoles().map((roleOption) => (
                  <MenuItem key={roleOption} value={roleOption}>
                    {roleOption}
                  </MenuItem>
                ))}
                {!getAvailableRoles().includes(role as any) && role && (
                  <MenuItem value={role}>
                    {role}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={disabled}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={disabled}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <TextField
              label="Price"
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={disabled}
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
            />
            <FormControl fullWidth>
              <InputLabel>Unit</InputLabel>
              <Select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                disabled={disabled}
                label="Unit"
              >
                <MenuItem value="hour">{UNITS.HOUR}</MenuItem>
                <MenuItem value="day">{UNITS.DAY}</MenuItem>
                <MenuItem value="month">{UNITS.MONTH}</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={shadow}
                  onChange={(e) => setShadow(e.target.checked)}
                  disabled={disabled}
                />
              }
              label="Shadow Role"
            />
            <Button
              variant="contained"
              onClick={handleAddEmployee}
              disabled={!selectedEmployee || disabled}
              sx={{ ml: 'auto' }}
            >
              Add Employee
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectEmployeeManager;
